import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import { Bar, Tooltip } from "recharts";
import { sessionsByProjectGroupSelector } from "../../../stats/state/statsSlice";
import type { DateRange } from "../DateRangePicker";
import { getSessionDate, isSessionInDateRange } from "../../utils/dateUtils";
import { useMonthWindow } from "../../hooks/useMonthWindow";
import SessionsPerMonthChartBase from "./SessionsPerMonthChartBase";

interface SessionsPerMonthChartProps {
  dateRange: DateRange;
  projectId?: number;
  projectGroupId?: number;
}

const PARCOURS_COLORS = [
  "#00c889",
  "#0c1e59",
  "#563cff",
  "#00b47b",
  "#55628b",
  "#8977ff",
  "#009667",
  "#9ea5bd",
  "#4d36e6",
  "#4dd9ac",
];

interface MonthData {
  month: number;
  year: number;
  label: string;
  fullLabel: string;
  sessions: number;
  [key: string]: number | string;
}

interface ParcoursInfo {
  id: number;
  name: string;
  color: string;
}

interface CustomStackedTooltipProps {
  active?: boolean;
  payload?: Array<{
    value?: number;
    dataKey?: string;
    color?: string;
    name?: string;
    payload?: MonthData;
  }>;
  label?: string;
  parcoursInfoList: ParcoursInfo[];
  t: (key: string) => string;
}

const CustomStackedTooltip = ({
  active,
  payload,
  label,
  parcoursInfoList,
  t,
}: CustomStackedTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;

  const monthData = payload[0]?.payload as MonthData;
  const fullLabel = monthData?.fullLabel || label;

  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  const sortedPayload = [...payload]
    .filter((entry) => (entry.value || 0) > 0)
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[180px]"
      style={{ maxHeight: "300px", overflowY: "auto" }}
    >
      <p className="font-semibold text-gray-900 mb-2 border-b pb-1">{fullLabel}</p>
      {sortedPayload.map((entry, index) => {
        const parcoursInfo = parcoursInfoList.find((p) => `parcours_${p.id}` === entry.dataKey);
        return (
          <div key={index} className="flex items-center gap-2 py-0.5">
            <span
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm truncate flex-1" style={{ color: entry.color }}>
              {parcoursInfo?.name || entry.name}
            </span>
            <span className="text-sm font-medium text-gray-700">{entry.value}</span>
          </div>
        );
      })}
      {sortedPayload.length > 0 && (
        <div className="border-t mt-2 pt-1 flex justify-between">
          <span className="text-sm font-medium text-gray-600">{t("statsNew.charts.total")}</span>
          <span className="text-sm font-bold text-gray-900">{total}</span>
        </div>
      )}
    </div>
  );
};

const SessionsPerMonthChart = ({
  dateRange,
  projectId,
  projectGroupId,
}: SessionsPerMonthChartProps) => {
  const { t } = useTranslation();
  const sessionsByProjectGroup = useAppSelector(sessionsByProjectGroupSelector);

  const showStackedBars = !projectGroupId && !projectId;

  const {
    viewEndDate,
    monthLabels,
    canGoNext,
    periodText,
    goToPrevMonth,
    goToNextMonth,
    goToPrevYear,
    goToNextYear,
  } = useMonthWindow(dateRange);

  const parcoursSessionCounts = useMemo(() => {
    if (!showStackedBars) return new Map<number, { name: string; count: number }>();

    const counts = new Map<number, { name: string; count: number }>();

    sessionsByProjectGroup.forEach((group) => {
      let groupCount = 0;

      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);

          if (!isSessionInDateRange(sessionDate, dateRange)) return;

          groupCount++;
        });
      });

      if (groupCount > 0) {
        counts.set(group.project_group_id, {
          name: group.project_group_name,
          count: groupCount,
        });
      }
    });

    return counts;
  }, [sessionsByProjectGroup, dateRange, showStackedBars]);

  const parcoursInfoList = useMemo((): ParcoursInfo[] => {
    if (!showStackedBars) return [];

    const sortedParcours = Array.from(parcoursSessionCounts.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10);

    return sortedParcours.map(([id, { name }], index) => ({
      id,
      name,
      color: PARCOURS_COLORS[index % PARCOURS_COLORS.length],
    }));
  }, [parcoursSessionCounts, showStackedBars]);

  const chartData = useMemo(() => {
    const months: MonthData[] = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - i, 1);
      const monthData: MonthData = {
        month: date.getMonth(),
        year: date.getFullYear(),
        label: `${monthLabels[date.getMonth()].slice(0, 3)} ${date
          .getFullYear()
          .toString()
          .slice(-2)}`,
        fullLabel: `${monthLabels[date.getMonth()]} ${date.getFullYear()}`,
        sessions: 0,
      };

      if (showStackedBars) {
        parcoursInfoList.forEach((parcours) => {
          monthData[`parcours_${parcours.id}`] = 0;
        });
      }

      months.push(monthData);
    }

    sessionsByProjectGroup.forEach((group) => {
      if (projectGroupId && group.project_group_id !== projectGroupId) return;

      const parcoursKey = `parcours_${group.project_group_id}`;
      const isTrackedParcours = parcoursInfoList.some((p) => p.id === group.project_group_id);

      group.projects.forEach((project) => {
        if (projectId && project.project_id !== projectId) return;

        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);

          if (!isSessionInDateRange(sessionDate, dateRange)) return;

          const sessionMonth = sessionDate.getMonth();
          const sessionYear = sessionDate.getFullYear();

          const monthData = months.find((m) => m.month === sessionMonth && m.year === sessionYear);
          if (monthData) {
            monthData.sessions++;

            if (showStackedBars && isTrackedParcours) {
              monthData[parcoursKey] = ((monthData[parcoursKey] as number) || 0) + 1;
            }
          }
        });
      });
    });

    return months;
  }, [
    sessionsByProjectGroup,
    dateRange,
    projectId,
    projectGroupId,
    monthLabels,
    viewEndDate,
    showStackedBars,
    parcoursInfoList,
  ]);

  const hasData = chartData.some((d) => d.sessions > 0);

  return (
    <SessionsPerMonthChartBase
      data={chartData}
      hasData={hasData}
      periodText={periodText}
      canGoNext={canGoNext}
      goToPrevMonth={goToPrevMonth}
      goToNextMonth={goToNextMonth}
      goToPrevYear={goToPrevYear}
      goToNextYear={goToNextYear}
    >
      {showStackedBars ? (
        <>
          <Tooltip
            content={(props: unknown) => (
              <CustomStackedTooltip
                {...(props as CustomStackedTooltipProps)}
                parcoursInfoList={parcoursInfoList}
                t={t}
              />
            )}
            cursor={{ fill: "rgba(0, 200, 137, 0.1)" }}
          />
          {[...parcoursInfoList].reverse().map((parcours) => (
            <Bar
              key={parcours.id}
              dataKey={`parcours_${parcours.id}`}
              name={parcours.name}
              stackId="parcours"
              fill={parcours.color}
              radius={[0, 0, 0, 0]}
              maxBarSize={40}
            />
          ))}
        </>
      ) : (
        <>
          <Tooltip
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
            }}
            cursor={{ fill: "rgba(0, 200, 137, 0.1)" }}
            formatter={(value: number) => [value, t("statsNew.charts.sessions")]}
          />
          <Bar dataKey="sessions" fill="#00c889" radius={[4, 4, 0, 0]} maxBarSize={40} />
        </>
      )}
    </SessionsPerMonthChartBase>
  );
};

export default SessionsPerMonthChart;
